<template>
  <div
    class="card-tarja-blogueirinha"
    :class="{
      'card-tarja-blogueirinha--fullscreen': isFullScreen,
      'card-tarja-blogueirinha--media': isMediaLayout,
    }"
    :style="cardStyle"
    :key="displayedItem.id_colaboracao"
  >
    <svg class="bloguerinha-layout__defs" width="0" height="0" aria-hidden="true">
      <defs>
        <filter :id="motionBlurFilterId" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur ref="motionBlurAnim" stdDeviation="0 0">
            <animate
              attributeName="stdDeviation"
              from="28 0"
              to="0 0"
              dur="1s"
              begin="indefinite"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0 0 0.58 1"
            />
          </feGaussianBlur>
        </filter>
      </defs>
    </svg>
    <div
      class="bloguerinha-layout"
      :class="[phaseClass, { 'bloguerinha-layout--media': isMediaLayout }]"
    >
      <div class="bloguerinha-layout__tarja" :style="tarjaStyle">
        <img
          v-if="!isMediaLayout"
          class="bloguerinha-layout__background"
          :src="backgroundUrl"
          alt=""
          aria-hidden="true"
        />
        <div
          class="bloguerinha-layout__lines"
          :class="{ 'bloguerinha-layout__lines--media': isMediaLayout }"
        >
          <div
            v-if="!hideUserInfo"
            class="bloguerinha-layout__bar bloguerinha-layout__bar--title"
            :style="{ fontSize: titleFontSize }"
          >
            <img
              class="bloguerinha-layout__avatar"
              :src="avatarUrl"
              :alt="`Avatar de ${headerText}`"
              @error="avatarLoadFailed = true"
            />
            <span class="bloguerinha-layout__title-text">{{ headerText }}</span>
          </div>
          <div
            class="bloguerinha-layout__bar bloguerinha-layout__bar--content"
            :class="{
              'bloguerinha-layout__bar--content--media': isVisualMedia,
              'bloguerinha-layout__bar--content--audio': isAudio,
            }"
            :style="{ fontSize: contentFontSize }"
          >
            <template v-if="isVisualMedia">
              <img
                v-if="displayedItem.dsc_tipo_midia_colaboracao === 'image'"
                :src="displayedItem.url_midia_colaboracao"
                :style="mediaBoundsStyle"
                alt="Mídia da colaboração"
              />
              <video
                v-else
                :key="displayedItem.url_midia_colaboracao"
                :src="displayedItem.url_midia_colaboracao"
                :style="mediaBoundsStyle"
                autoplay
                muted
                playsinline
              ></video>
            </template>
            <audio
              v-else-if="isAudio"
              :key="displayedItem.url_midia_colaboracao"
              controls
              controlslist="nodownload"
              autoplay
            >
              <source :src="displayedItem.url_midia_colaboracao" />
            </audio>
            <span v-else>{{ contentText }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// URL do asset (regra asset/resource no vue.config.js — não passa pelo
// vue-svg-loader para evitar minificação/colisão de ids no DOM).
import tarjaSocialcamUrl from "@/assets/bloguerinha/tarja_socialcam.svg";
import defaultUserAvatar from "@/assets/User.png";

// Durações das animações de entrada/saída da tarja. A entrada casa com
// --tarja-anim-enter no CSS e com a duração do <animate> do motion blur
// (dur="1s" no SVG acima); a saída casa com --tarja-anim-leave e com a
// duração de leave do <transition name="danca-huck-tarja"> no TarjaView.
const ENTER_DURATION_MS = 1000;
const LEAVE_DURATION_MS = 600;

export default {
  name: "CardTarjaBlogueirinha",
  props: {
    item: {
      type: Object,
      required: true,
    },
    posicaoCard: {
      type: String,
      default: "esquerda",
    },
    isFullScreen: Boolean,
    hideUserInfo: Boolean,
    flgExibirUf: Boolean,
  },
  data() {
    return {
      backgroundUrl: tarjaSocialcamUrl,
      // Id único do filtro de motion blur: o SVG inline registra o filtro
      // globalmente no documento e a saída animada do <transition> mantém o
      // card anterior no DOM, então o id precisa ser por instância.
      motionBlurFilterId: `bloguerinha-motion-blur-${this._uid}`,
      avatarLoadFailed: false,
      midiaOrientation: null,
      // Item efetivamente exibido. Na troca de colaboração a tarja atual
      // sai animada (desliza para fora à direita) e só depois exibe o
      // próximo item (mesmo fluxo do CardTarjaDancaHuck).
      displayedItem: this.item,
      // Fase da animação da tarja: "entering" | "shown" | "leaving".
      animationPhase: "entering",
      pendingItem: null,
      animationTimer: null,
      // Redimensionamento com mídia igual ao CardTarjaDancaHuck (que copia
      // do CardTarjaPanelaSportv): porcentagem da tela que o card e a mídia
      // podem ocupar.
      percentScreenDim: {
        maxWidth: 30,
        minWidth: 20,
        minHeight: 40,
        maxHeight: 70,
        fullScreenMaxWidth: 100,
        fullScreenMaxHeight: 100,
        fullScreenMinWidth: 100,
        fullScreenMinHeight: 100,
      },
    };
  },
  created() {
    // Pré-aquece o cache do background: a tarja monta quando a colaboração
    // chega via API e o <img> precisa já estar resolvido para a entrada não
    // piscar.
    const warmup = new Image();
    warmup.src = tarjaSocialcamUrl;
    this.playEnter();
    if (this.displayedItem.url_midia_colaboracao) {
      this.getMidiaOrientation();
    }
  },
  beforeDestroy() {
    this.clearAnimationTimer();
  },
  computed: {
    hasMedia() {
      return Boolean(this.displayedItem.url_midia_colaboracao);
    },
    isVisualMedia() {
      return (
        this.hasMedia &&
        ["image", "video"].includes(this.displayedItem.dsc_tipo_midia_colaboracao)
      );
    },
    isAudio() {
      return (
        this.hasMedia && this.displayedItem.dsc_tipo_midia_colaboracao === "audio"
      );
    },
    // Imagem/vídeo/áudio usam o layout em coluna proporcional ao card
    // (mesmo fluxo do CardTarjaDancaHuck); só o texto mantém a tarja 16/9
    // original com a barra dourada do SVG.
    isMediaLayout() {
      return this.isVisualMedia || this.isAudio;
    },
    headerText() {
      if (this.flgExibirUf && this.displayedItem.dsc_uf_colaborador) {
        return `${this.displayedItem.dsc_nome_colaborador} - ${this.displayedItem.dsc_uf_colaborador}`;
      }
      return this.displayedItem.dsc_nome_colaborador || "";
    },
    contentText() {
      return this.displayedItem.dsc_texto_colaboracao || "";
    },
    avatarUrl() {
      if (
        this.avatarLoadFailed ||
        this.displayedItem.flg_sem_avatar !== "N" ||
        !this.displayedItem.url_avatar_colaborador
      ) {
        return defaultUserAvatar;
      }
      return this.displayedItem.url_avatar_colaborador;
    },
    titleFontSize() {
      const titleLength = this.headerText.trim().length;
      if (titleLength > 32) return "clamp(0.6rem, 1.15vw, 22px)";
      if (titleLength > 20) return "clamp(0.65rem, 1.3vw, 25px)";
      return "clamp(0.7rem, 1.45vw, 27px)";
    },
    contentFontSize() {
      return "clamp(0.6rem, 1.2vw, 22px)";
    },
    screenDimensions() {
      return {
        width: this.$vuetify.breakpoint.width,
        height: this.$vuetify.breakpoint.height,
      };
    },
    // Limites da mídia (imagem/vídeo), mesmos cálculos do CardTarjaDancaHuck.
    midiaSize() {
      const { width, height } = this.screenDimensions;
      const { maxWidth, maxHeight } = this.percentScreenDim;
      if (this.isFullScreen) {
        return { width: width - 20, height: height - 100 };
      }
      if (this.midiaOrientation === "vertical") {
        return {
          width: (maxWidth * width) / 100 - 20,
          height: (maxHeight * height) / 100,
        };
      }
      return {
        width: (maxWidth * width) / 100 - 20,
        height: (maxHeight * height) / 100 - 100,
      };
    },
    mediaBoundsStyle() {
      return {
        maxWidth: `${this.midiaSize.width}px`,
        maxHeight: `${this.midiaSize.height}px`,
      };
    },
    // Classe de animação da tarja conforme a fase: entrada desliza da
    // esquerda, saída desliza para a direita.
    phaseClass() {
      if (this.animationPhase === "entering") {
        return "bloguerinha-layout--entering";
      }
      if (this.animationPhase === "leaving") {
        return "bloguerinha-layout--leaving";
      }
      return null;
    },
    // Motion blur acoplado à entrada: filtro SVG (blur horizontal) aplicado
    // somente durante a entrada. A intensidade decai via <animate> (SMIL)
    // sincronizado com a animação CSS — ambos 1s ease-out.
    tarjaStyle() {
      if (this.animationPhase !== "entering") {
        return {};
      }
      return { filter: `url(#${this.motionBlurFilterId})` };
    },
    cardStyle() {
      const positionStyles = {
        esquerda: {
          justifySelf: "start",
          marginLeft: "0",
        },
        centro: {
          justifySelf: "center",
        },
        direita: {
          justifySelf: "end",
          marginRight: "0",
        },
      };

      if (this.isFullScreen) {
        if (this.hasMedia) {
          const { width, height } = this.screenDimensions;
          const {
            fullScreenMaxWidth,
            fullScreenMaxHeight,
            fullScreenMinWidth,
            fullScreenMinHeight,
          } = this.percentScreenDim;
          return {
            justifySelf: "center",
            maxWidth: `${(fullScreenMaxWidth * width) / 100}px !important`,
            maxHeight: `${(fullScreenMaxHeight * height) / 100}px !important`,
            minWidth: `${(fullScreenMinWidth * width) / 100}px !important`,
            minHeight: `${(fullScreenMinHeight * height) / 100}px !important`,
          };
        }
        // Texto em tela cheia mantém a caixa 16/9 da classe --fullscreen,
        // apenas posicionada.
        return positionStyles[this.posicaoCard] || positionStyles.esquerda;
      }

      if (!this.hasMedia) {
        return positionStyles[this.posicaoCard] || positionStyles.esquerda;
      }

      // Com mídia (imagem/vídeo/áudio), o posicionamento segue as mesmas
      // regras do CardTarjaDancaHuck/CardTarjaPanelaSportv. Sem max-height
      // no card: a mídia já é limitada por midiaSize. A margem inferior
      // garante o respiro fixo de 10px da borda (o wrapper do TarjaView
      // alinha ao fim).
      const { width } = this.screenDimensions;
      const { maxWidth } = this.percentScreenDim;
      const mediaPositionStyles = {
        esquerda: {
          justifySelf: "flex-start",
          marginLeft: "120px",
        },
        centro: {
          justifySelf: "center",
        },
        direita: {
          justifySelf: "flex-start",
          marginRight: "60px",
        },
      };
      const position =
        mediaPositionStyles[this.posicaoCard] || mediaPositionStyles.esquerda;

      if (this.isAudio) {
        return {
          ...position,
          maxWidth: `${Math.min(640, (maxWidth * width) / 100)}px`,
          marginBottom: "10px",
        };
      }

      return {
        ...position,
        maxWidth: `${(maxWidth * width) / 100}px`,
        marginBottom: "10px",
      };
    },
  },
  methods: {
    // Detecção de orientação da mídia (vertical x horizontal), igual ao
    // CardTarjaDancaHuck: define os limites aplicados em mediaBoundsStyle.
    getMidiaOrientation() {
      if (this.displayedItem.dsc_tipo_midia_colaboracao === "image") {
        const image = new Image();
        image.src = this.displayedItem.url_midia_colaboracao;
        image.onload = () => {
          this.midiaOrientation =
            image.height > image.width ? "vertical" : "horizontal";
        };
      } else if (this.displayedItem.dsc_tipo_midia_colaboracao === "video") {
        const createdVideoElement = document.createElement("video");
        createdVideoElement.src = this.displayedItem.url_midia_colaboracao;
        createdVideoElement.style.visibility = "hidden";
        createdVideoElement.style.position = "absolute";
        createdVideoElement.style.width = "0";
        createdVideoElement.style.height = "0";
        document.body.appendChild(createdVideoElement);
        createdVideoElement.addEventListener("loadedmetadata", () => {
          this.midiaOrientation =
            createdVideoElement.videoHeight > createdVideoElement.videoWidth
              ? "vertical"
              : "horizontal";
          document.body.removeChild(createdVideoElement);
        });
      }
    },
    // Entrada: tarja desliza da esquerda para a direita (1s, ease-out) com
    // fade-in e motion blur. Ao final, se uma nova colaboração chegou
    // durante a entrada, encerra este card para exibi-la.
    playEnter() {
      this.animationPhase = "entering";
      this.playMotionBlur();
      this.setAnimationTimer(() => {
        this.animationPhase = "shown";
        if (this.pendingItem) {
          this.startLeave();
        }
      }, ENTER_DURATION_MS);
    },
    // Saída: tarja desliza para fora pela direita (0.6s, ease-in) e só
    // então o item pendente (o mais recente recebido) passa a ser exibido.
    startLeave() {
      this.animationPhase = "leaving";
      this.setAnimationTimer(() => {
        const nextItem = this.pendingItem;
        this.pendingItem = null;
        this.displayedItem = nextItem;
        this.avatarLoadFailed = false;
        this.midiaOrientation = null;
        if (nextItem.url_midia_colaboracao) {
          this.getMidiaOrientation();
        }
        this.playEnter();
      }, LEAVE_DURATION_MS);
    },
    // Dispara o <animate> do blur (SMIL) após o DOM existir. A raiz do card
    // é re-keyed a cada troca de colaboração, recriando os nós — por isso o
    // beginElement é reexecutado a cada entrada.
    playMotionBlur() {
      this.$nextTick(() => {
        const motionBlurAnim = this.$refs.motionBlurAnim;
        if (
          motionBlurAnim &&
          typeof motionBlurAnim.beginElement === "function"
        ) {
          motionBlurAnim.beginElement();
        }
      });
    },
    setAnimationTimer(callback, durationMs) {
      this.clearAnimationTimer();
      this.animationTimer = setTimeout(callback, durationMs);
    },
    clearAnimationTimer() {
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }
    },
  },
  watch: {
    // O TarjaView reaproveita esta instância entre colaborações (o card é
    // keyed por produção, não por colaboração). Ao trocar o item, a tarja
    // atual sai animada e só então o novo entra; atualizações do mesmo
    // item aparecem sem animação.
    item(newItem) {
      if (!newItem || !newItem.id_colaboracao) {
        return;
      }
      if (newItem.id_colaboracao === this.displayedItem.id_colaboracao) {
        this.displayedItem = newItem;
        return;
      }
      this.pendingItem = newItem;
      if (this.animationPhase === "shown") {
        this.startLeave();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Quiche Sans";
  src: url("@/assets/bloguerinha/quichesans-bold.otf") format("opentype");
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: "Globo Tx Lt";
  src: url("@/assets/bloguerinha/Globo Tx Lt.otf") format("opentype");
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

.card-tarja-blogueirinha {
  position: relative;
  width: min(100vw, 1440px);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  // Duração da animação de entrada: casa com ENTER_DURATION_MS no script e
  // com dur="1s" do <animate> do motion blur.
  --tarja-anim-enter: 1s;
  --tarja-anim-leave: 0.6s;
  // Respiro interno e raio do bloco único de background da mídia (layout
  // em coluna), com os mesmos valores do CardTarjaDancaHuck. Consumidos
  // apenas pelo modo --media abaixo.
  --tarja-bar-radius: min(1.8vw, 26px);
  --tarja-content-pad-y: min(1.25vw, 18px);
  --tarja-pad-x: min(1.5vw, 21px);
}

.card-tarja-blogueirinha--media {
  aspect-ratio: auto;
  height: auto;
  overflow: visible;
  // Card de mídia ocupa só 20–30% da tela: raio proporcional ao card.
  --tarja-bar-radius: 0.6vw;
}

.card-tarja-blogueirinha--fullscreen {
  flex: none;
  width: min(100vw, calc(100vh * 16 / 9));
  max-height: 100vh;
}

.card-tarja-blogueirinha--fullscreen.card-tarja-blogueirinha--media {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  // Em tela cheia o card volta à escala cheia da tela.
  --tarja-bar-radius: min(1.8vw, 26px);
}

.bloguerinha-layout {
  position: relative;
  width: 100%;
  height: 100%;
}

.bloguerinha-layout--media {
  height: auto;
}

.bloguerinha-layout__tarja {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

// Mídia (imagem/vídeo/áudio): a tarja deixa de ser um recorte fixo do
// quadro 16/9 e vira uma coluna com altura automática (as barras de título
// e conteúdo definem o tamanho do card).
.bloguerinha-layout--media .bloguerinha-layout__tarja {
  position: static;
  overflow: visible;
}

// Background full-frame (1920x1080): a barra dourada fica no rodapé do
// quadro (y 926.65→1026.57, x 98.62→1822.64 do viewBox). Renderizado só no
// modo texto; no modo mídia as barras usam o gradiente dourado em CSS.
.bloguerinha-layout__background {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

// Duas linhas compostas sobre a barra dourada, medida no viewBox:
// left 98.62/1920, width 1724.02/1920, top 926.65/1080, height 99.92/1080.
.bloguerinha-layout__lines {
  position: absolute;
  top: 85.8%;
  left: 5.13%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  width: 89.79%;
  height: 9.25%;
  padding-inline: 1.2%;
  gap: 0.3%;
  text-align: left;
  line-height: 1.1;
}

// Tarja de título: avatar + nome (mesmo formato do CardTarjaDancaHuck).
.bloguerinha-layout__bar--title {
  display: flex;
  align-items: center;
  gap: 1.5%;
  min-width: 0;
  overflow: hidden;
  color: #3d2b1f;
  font-family: "Quiche Sans", sans-serif;
  font-weight: 700;
  text-transform: uppercase;
}

.bloguerinha-layout__title-text {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.bloguerinha-layout__avatar {
  flex: none;
  width: min(2.2vw, 32px);
  height: min(2.2vw, 32px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  object-fit: cover;
}

.bloguerinha-layout__bar--content {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #3d2b1f;
  font-family: "Globo Tx Lt", sans-serif;
  font-weight: 300;
}

// App.vue tem uma regra global "p, span, label { color: var(--v-text_secondary-base) }"
// que casa diretamente com os <span> de texto e vence a herança das barras.
// "inherit" devolve a cor da barra (#3d2b1f) com specificity maior que a global.
.bloguerinha-layout__bar span {
  color: inherit;
}

// Mídia (imagem/vídeo/áudio): título e conteúdo seguem em seções separadas
// (mesma estrutura do CardTarjaDancaHuck), mas sobre UM ÚNICO bloco de
// background: o container __lines vira o retângulo dourado único e as
// barras ficam transparentes, sem borda/raio/sombra próprios. Gradiente do
// tarja_socialcam.svg replicado em CSS — se o gradiente do SVG mudar,
// atualizar aqui também.
.bloguerinha-layout__lines--media {
  position: static;
  top: auto;
  left: auto;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: var(--tarja-content-pad-y);
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: var(--tarja-bar-radius, 26px);
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.35);
  background: linear-gradient(90deg, #e7b14f 0%, #f0dfa2 100%);
  padding: var(--tarja-content-pad-y) var(--tarja-pad-x);
  overflow: hidden;
  text-align: left;
}

.bloguerinha-layout__lines--media .bloguerinha-layout__avatar {
  width: min(5.5vw, 48px);
  height: min(5.5vw, 48px);
}

.bloguerinha-layout__lines--media .bloguerinha-layout__bar--content {
  display: flex;
  align-items: center;
  white-space: normal;
  word-break: break-word;
}

.bloguerinha-layout__bar--content--media,
.bloguerinha-layout__bar--content--audio {
  justify-content: center;
}

.bloguerinha-layout__bar--content--media img,
.bloguerinha-layout__bar--content--media video {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.bloguerinha-layout__bar--content audio {
  width: 100%;
  max-width: 100%;
}

.card-tarja-blogueirinha--fullscreen.card-tarja-blogueirinha--media
  .bloguerinha-layout {
  flex: none;
  width: 100%;
}

// Entrada (1s, ease-out): desliza da esquerda para a direita com fade-in. O
// motion blur horizontal (filtro SVG) é acoplado via tarjaStyle enquanto a
// fase é "entering".
.bloguerinha-layout--entering .bloguerinha-layout__tarja {
  animation: bloguerinha-tarja-slide-in var(--tarja-anim-enter) ease-out both;
}

// Saída (0.6s, ease-in): continua o movimento para a direita saindo do
// quadro, com fade-out.
.bloguerinha-layout--leaving .bloguerinha-layout__tarja {
  animation: bloguerinha-tarja-slide-out var(--tarja-anim-leave) ease-in both;
}

// Saída por desmontagem (TarjaView remove o card via
// <transition name="danca-huck-tarja">): a classe de leave chega pela raiz
// externa e dispara a mesma saída.
.danca-huck-tarja-leave-active .bloguerinha-layout__tarja {
  animation: bloguerinha-tarja-slide-out var(--tarja-anim-leave) ease-in both;
}

@keyframes bloguerinha-tarja-slide-in {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes bloguerinha-tarja-slide-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(105%);
    opacity: 0;
  }
}
</style>
